import { InvoiceStatus } from '@/react_app/components/BillingUsage/InvoiceHistory/types';
export { UsageHistoryAnalytics } from '@/react_app/components/BillingUsage/UsageHistory/types';
/* eslint-disable @typescript-eslint/no-explicit-any */
enum TypeName {
	product = 'Product',
	plan = 'Plan',
	feature = 'Feature',
	money = 'Money',
	priceTier = 'PriceTier',
	packageEntitlement = 'PackageEntitlement',
}

interface PaginatedResponse<T> {
	next: string;
	results: T[];
	previous: string;
	has_next: boolean;
}
export interface InvoiceHistoryResponse extends PaginatedResponse<Invoice[]> {}
export interface SubscriptionHistoryResponse extends PaginatedResponse<Subscription[]> {}
export interface ProductAutomatedSubscriptionChangesResponse extends PaginatedResponse<ProductAutomatedSubscriptionChangesLine[]> {}

export interface ProductAutomatedSubscriptionChangesLine {
	//TODO-billing
	created_at: string;
	updated_at: string;
	is_billing_overages_enabled: boolean;
	billing_period: BillingPeriod;
	subscription_event: SubscriptionEvent;
	subscription_start_date: string;
	product_id: ProductType;
	plan_id: string;
	unit_quantity: number;
	addons: unknown[];
	metered_entitlement_before: EntitlementObject;
	metered_entitlement_after: EntitlementObject;
	users_entitlements_before: Record<string, EntitlementObject>;
	users_entitlements_after: Record<string, EntitlementObject>;
}

export interface SingleProductPurchaseDetails {
	planId: string;
	quantity: number;
	productType: ProductType;
}

export interface Invoice {
	// Enrich if needed
	id: string;
	object: string;
	account_country: string;
	account_name: string;
	account_tax_ids: unknown;
	amount_due: number;
	amount_paid: number;
	amount_remaining: number;
	amount_shipping: number;
	application: unknown;
	application_fee_amount: unknown;
	attempt_count: number;
	attempted: boolean;
	auto_advance: boolean;
	automatic_tax: {
		enabled: boolean;
		liability: unknown;
		status: unknown;
	};
	billing_reason: string;
	charge: string;
	collection_method: string;
	created: number;
	currency: string;
	custom_fields: unknown;
	customer: string;
	customer_address: unknown;
	customer_email: string;
	customer_name: string;
	customer_phone: unknown;
	customer_shipping: unknown;
	customer_tax_exempt: string;
	customer_tax_ids: unknown[];
	default_payment_method: unknown;
	default_source: unknown;
	default_tax_rates: unknown[];
	description: unknown;
	discount: unknown;
	discounts: unknown[];
	due_date: unknown;
	effective_at: number;
	ending_balance: number;
	footer: unknown;
	from_invoice: unknown;
	hosted_invoice_url: string;
	invoice_pdf: string;
	issuer: {
		type: string;
	};
	last_finalization_error: unknown;
	latest_revision: unknown;

	lines: {
		object: string;
		data: unknown[];
		has_more: boolean;
		total_count: number;
		url: string;
	};
	livemode: boolean;
	metadata: unknown;
	next_payment_attempt: unknown;
	number: string;
	on_behalf_of: unknown;
	paid: boolean;

	paid_out_of_band: boolean;
	payment_intent: string;
	payment_settings: {
		default_mandate: unknown;
		payment_method_options: unknown;
		payment_method_types: unknown;
	};
	period_end: number;
	period_start: number;
	post_payment_credit_notes_amount: number;
	pre_payment_credit_notes_amount: number;
	quote: unknown;
	receipt_number: unknown;
	rendering: unknown;
	shipping_cost: unknown;
	shipping_details: unknown;
	starting_balance: number;
	statement_descriptor: unknown;
	status: InvoiceStatus;
}
export interface Customer {
	// Enrich if needed
}
export interface Portal {
	// Enrich if needed
}
export interface CustomerData {
	portal: Portal;
	customer: Customer;
	lite_users_count: number;
	premium_users_count: number;
	standard_users_count: number;
}

export enum PricingType {
	free = 'FREE',
	paid = 'PAID',
	custom = 'CUSTOM',
}

enum FeatureType {
	number = 'NUMBER',
	boolean = 'BOOLEAN',
}

enum MeterType {
	incremental = 'Incremental',
	none = 'None',
}

enum ResetPeriod {
	month = 'MONTH',
	yearly = 'YEARLY',
}

export enum SubscriptionStatus {
	active = 'ACTIVE',
	inactive = 'INACTIVE',
	cancelled = 'CANCELLED',
}

enum PaymentCollectionStatus {
	required = 'REQUIRED',
	notRequired = 'NOT_REQUIRED',
}

enum SubscriptionSetup {
	downgradeToFree = 'DOWNGRADE_TO_FREE',
	endOfBillingPeriod = 'END_OF_BILLING_PERIOD',
	planSelection = 'PLAN_SELECTION',
}

enum Status {
	published = 'PUBLISHED',
	new = 'NEW',
}

export enum BillingPeriod {
	annually = 'ANNUALLY',
	monthly = 'MONTHLY',
}

enum TiersMode {
	volume = 'VOLUME',
}

interface ProductDetail extends IDisplayable, IRefable {
	additionalMetaData?: any;
	productSettings?: ProductSettings;
}

interface IDisplayable {
	displayName: string;
	description?: string;
}

interface IRefable {
	refId: string;
}

interface ITyped {
	__typename: TypeName;
}

interface Feature extends IDisplayable, IRefable, ITyped {
	featureType: FeatureType;
	meterType: MeterType;
	featureUnits?: string;
	featureUnitsPlural?: string;
	hasMeter: boolean;
}

interface BasePlanDetail extends IDisplayable, IRefable {}

export interface Plan extends IDisplayable, IRefable, ITyped {
	id: string;
	status?: Status;
	updatedAt?: string;
	isLatest?: boolean;
	hiddenFromWidgets: string[];
	versionNumber: number;
	defaultTrialConfig?: any;
	draftSummary?: any;
	basePlan?: BasePlanDetail;
	entitlements: Entitlement[];
	inheritedEntitlements: Entitlement[];
	prices: Price[];
	pricingType: PricingType;
}

// Plan specific to subscribed products (subscription context)

interface ProductSettings extends ITyped {
	subscriptionEndSetup: SubscriptionSetup;
	subscriptionCancellationTime: SubscriptionSetup;
	downgradePlan: Plan;
	subscriptionStartSetup: SubscriptionSetup;
	subscriptionStartPlan?: Plan;
}

export interface Entitlement {
	usageLimit?: number;
	hasUnlimitedUsage: boolean;
	hasSoftLimit: boolean;
	featureId: string;
	resetPeriod?: ResetPeriod;
	hiddenFromWidgets: string[];
	isCustom?: boolean;
	displayNameOverride?: string;
	feature: FeatureDetail;
}

interface FeatureDetail extends IDisplayable, IRefable {
	featureType: FeatureType;
	meterType: MeterType;
	featureUnits?: string;
	featureUnitsPlural?: string;
}

export interface Price {
	id?: string;
	billingModel?: string;
	billingPeriod?: BillingPeriod;
	billingCountryCode?: string;
	price?: Money;
	tiersMode?: TiersMode;
	tiers?: PriceTier[];
	featureId?: string;
	packageId?: string;
	crmId?: string;
	crmLinkUrl?: string;
	minUnitQuantity?: number;
	maxUnitQuantity?: number;
	feature?: Feature;
	usedInSubscriptions?: boolean;
	usageLimit?: number;
}

export interface PriceTier extends ITyped {
	upTo: number;
	unitPrice?: number;
	flatPrice?: Money;
}

interface Money extends ITyped {
	amount: number;
	currency: string;
	billingPeriod?: BillingPeriod;
}
export interface SubscribedPlan extends Plan {
	billingId?: string; // Specific to subscriptions
	product: ProductDetail;
	compatibleAddons?: any[];
	compatiblePackageGroups?: any[];
	overagePrices?: Price[];
}

export interface Product extends ITyped {
	id: string;
	createdAt: string;
	displayName: string;
	refId: string;
	environmentId: string;
	updatedAt: string;
	isDefaultProduct: boolean;
	additionalMetaData?: any;
	multipleSubscriptions: boolean;
	awsMarketplaceProductId?: any;
	awsMarketplaceProductCode?: any;
	productSettings: ProductSettings;
	plans: Plan[];
	addons: any[];
}
export interface Subscription {
	id: string;
	subscriptionId: string;
	startDate: string;
	endDate?: string;
	trialEndDate?: string;
	cancellationDate?: string;
	effectiveEndDate?: string;
	status: SubscriptionStatus;
	refId: string;
	currentBillingPeriodEnd?: string;
	additionalMetaData?: any;
	billingId?: string;
	billingLinkUrl?: string;
	latestInvoice?: any;
	paymentCollection: PaymentCollectionStatus;
	billingSyncError?: any;
	resource?: any;
	experimentInfo?: any;
	prices: Price[];
	totalPrice?: number;
	pricingType: PricingType;
	plan: SubscribedPlan;
	addons: any[];
	scheduledUpdates: any[];
	futureUpdates: any[];
}
export enum ProductType {
	Credits = 'product-credits',
	TeamFeatures = 'product-team-features',
	Automation = 'product-automation-tasks',
	CustomerFeatures = 'product-customer-features',
}

export enum OveragesProductType {
	Credits = 'product-credits-overages',
	Automation = 'product-automation-tasks-overages',
	CustomerFeatures = 'product-customer-features-overages',
}

export enum PlanTypes {
	Credits = 'plan-credits',
	TeamFeatures = 'plan-team-features',
	Automation = 'plan-automation-tasks',
	CustomerFeatures = 'plan-customer-features',
}

export enum FeatureTypes {
	UnblockBetaPlans = 'feature-unblock-beta-plans',

	PremiumUsersCount = 'feature-premium-users',
	StandardUsersCount = 'feature-standard-users',
	LiteUsersCount = 'feature-lite-users',
	Credits = 'feature-credits',
	CreditsOverages = 'feature-credits-overages',
	AutomationTasks = 'feature-automation-tasks',
	AutomationTasksOverages = 'feature-automation-tasks-overages',
	TeamTasks = 'feature-team-tasks',
	TaskLogDaysToKeep = 'feature-task-log-days',
	AnalyticsDaysToQuery = 'feature-analytics-days',
	CustomerTasks = 'feature-customer-tasks',
	CustomerTasksOverages = 'feature-customer-tasks-overages',
	SSO = 'feature-sso',
	SharedProjects = 'feature-shared-projects',
	Groups = 'feature-groups',
	SyncedGroups = 'feature-synced-groups',
	GroupUsagePermissions = 'feature-group-usage-permissions',
	SubFlows = 'feature-subflows',
	UserHierarchy = 'feature-user-hierarchy',
	CustomerCustomCodeActions = 'feature-customer-custom-code-actions',
	TeamCustomCodeActions = 'feature-team-custom-code-actions',
	CustomerOutgoingApiActions = 'feature-customer-outgoing-api-actions',
	TeamOutgoingApiActions = 'feature-team-outgoing-api-actions',
	BetaReleases = 'feature-beta-releases',
	SensitiveConnections = 'feature-sensitive-connections',
	VersionHistory = 'feature-version-history',
	CreatorSpacePermissions = 'feature-creator-space-permissions',
	CustomerCustomDomains = 'feature-customer-custom-domains',
	CustomerCustomEmailDomains = 'feature-customer-custom-email-domains',
	CustomerIframeBlock = 'feature-customer-iframe-block',
	CustomerMiniApps = 'feature-customer-mini-apps',
	CustomerRedirect = 'feature-customer-redirect',
	CustomerUrlParameters = 'feature-customer-url-parameters',
	CustomerExtraPremiumApps = 'feature-customer-extra-premium',
	SharedNotificationInboxes = 'feature-shared-notification-inboxes',
	CustomerAuthCookieRetention = 'feature-customer-auth-cookie-retention-policy',
	CustomerAuthEmailSignIn = 'feature-customer-auth-sign-in-with-email',
	CustomerAuthSMSSignIn = 'feature-customer-auth-sign-in-with-sms',
	CustomerMinimalBranding = 'feature-customer-minimal-branding',
	CustomerWhiteLabel = 'feature-customer-white-label',
}

export enum BillingErrorCodes {
	UpgradeSubscription = 'upgrade_subscription',
	UsageLimitExceeded = 'usage_limit_exceeded',
	ContextTypeNotAllowed = 'context_type_not_allowed',
	LiteSharedUser = 'workflow-0066',
	DowngradingError = 'billing-0007',
	CannotUseBetaPlan = 'billing-0011',
}

export enum BillingLevel {
	LiteMember = 'lite',
	PremiumMember = 'premium',
	StandardMember = 'standard',
}
export interface BillingError {
	msg: string;
	metadata: {
		reason?: FeatureTypes;
		reasons?: FeatureTypes[];
		billing_level: BillingLevel;
		plan_required: {
			plan_id: string;
			product_id: ProductType;
		};
	};
}
export enum ServiceActionRestrictionTypes {
	CustomCode = 'custom_code',
	OutgoingApis = 'outgoing_apis',
	MiniApps = 'mini_apps',
	RedirectUrl = 'redirect_url',
	ExtraPremium = 'extra_premium',
}

export type Subscriptions = Record<string, Subscription>;
export type PriceMap = Record<number | string, number | undefined>;

export interface PurchaseDetails {
	billingPeriod: BillingPeriod;
	teamFeature: {
		id: string;
	};
	customerFeature: {
		id: string;
		quantity: number;
	};
	automationTasks: {
		quantity: number;
	};
	credits: {
		quantity: number;
	};
}
export enum PlanGroups {
	Beta = 'beta',
	Free = 'free',
	Plus = 'plus',
	Scale = 'scale',
	Engage = 'engage',
	Business = 'business',
	Enterprise = 'enterprise',
}

export interface SelectOption {
	text: string;
	value: number;
	suffixText?: string;
}

export interface PaymentIntent {
	intent: {
		id: string;
		object: string;
		application: any;
		automatic_payment_methods: {
			allow_redirects: string;
			enabled: boolean;
		};
		cancellation_reason: any;
		client_secret: string;
		created: number;
		customer: any;
		description: any;
		flow_directions: any;
		last_setup_error: any;
		latest_attempt: any;
		livemode: boolean;
		mandate: any;
		metadata: Record<string, any>;
		next_action: any;
		on_behalf_of: any;
		payment_method: any;
		payment_method_configuration_details: {
			id: string;
			parent: any;
		};
		payment_method_options: {
			card: {
				mandate_options: any;
				network: any;
				request_three_d_secure: string;
			};
		};
		payment_method_types: string[];
		single_use_mandate: any;
		status: string;
		usage: string;
	};
}
export interface PricePreviewInput {
	plan_id?: string;
	start_date?: string;
	unit_quantity?: number;
	promotion_code?: string;
	product_id: ProductType;
	billing_period: BillingPeriod;
}

export interface PaymentMethod {
	id: string;
	object: string;
	allow_redisplay: string;
	billing_details: BillingDetails;
	card: CardDetails;
	created: number;
	customer: string;
	livemode: boolean;
	metadata: Record<string, unknown>;
	type: string;
}
export interface Card {
	last4: string;
	brand: string;
	exp_year: string;
	exp_month: string;
}
export type PaymentMethodId = string;

interface BillingDetails {
	address: Address;
	email: string | null;
	name: string | null;
	phone: string | null;
}

interface Address {
	city: string | null;
	country: string;
	line1: string | null;
	line2: string | null;
	postal_code: string | null;
	state: string | null;
}

interface CardDetails {
	brand: string;
	checks: CardChecks;
	country: string;
	display_brand: string;
	exp_month: number;
	exp_year: number;
	fingerprint: string;
	funding: string;
	generated_from: string | null;
	last4: string;
	networks: CardNetworks;
	three_d_secure_usage: ThreeDSecureUsage;
	wallet: string | null;
}

interface CardChecks {
	address_line1_check: string | null;
	address_postal_code_check: string | null;
	cvc_check: string;
}

interface CardNetworks {
	available: string[];
	preferred: string | null;
}

interface ThreeDSecureUsage {
	supported: boolean;
}
enum Currency {
	USD = 'USD',
}

interface MonetaryValue {
	amount: number;
	currency: Currency;
}

interface InvoiceDetails {
	total: MonetaryValue;
	sub_total: MonetaryValue;
	tax: MonetaryValue | null;
	discount: MonetaryValue | null;
	tax_details: TaxDetails | null;
	total_excluding_tax: MonetaryValue;
	discount_details: DiscountDetails | null;
	credits?: any; // Replace 'any' with the appropriate type if possible
	proration?: any; // Replace 'any' with the appropriate type if possible
}
interface DiscountDetails {
	value: number;
	type: DiscountType;
	duration_type: DurationType;
	duration_in_months: number | null;
}
export enum DiscountType {
	AMOUNT = 'AMOUNT',
	PERCENTAGE = 'PERCENTAGE',
}
enum DurationType {
	FOREVER = 'FOREVER',
	LIMITED = 'LIMITED',
}

interface TaxDetails {
	// Assuming structure based on your potential future needs
}

interface DateRange {
	end: string;
	start: string;
}

export interface ProductInvoicePreview {
	preview_subscription: {
		is_plan_downgrade: boolean;
		has_scheduled_updates: boolean;
		billing_period_range: DateRange;
		recurring_invoice: InvoiceDetails;
		immediate_invoice: InvoiceDetails;
	};
}
export interface PricePreviewInputParams {
	planId?: string;
	unitQuantity?: number;
	productId: ProductType;
	billingPeriod: BillingPeriod;
}
export interface ProductPricesPreview {
	dueToday: number;
	upComingInvoice: number;
}

export interface NextPlanForEntitlement {
	plan_id: string;
	product_id: ProductType;
}

export interface Entitlement {
	type: string;
	usage_limit: number;
	current_usage: number;
	is_unlimited: boolean;
}

export interface EntitlementObject {
	has_access: boolean;
	entitlement: Entitlement;
}
export interface UpgradePlanDialogProps {
	planId: string;
	productId: ProductType;
	featureId: FeatureTypes;
	canEditBilling?: boolean;
	upgradeNeededExplanation?: string;
}

export type EntitlementResponse = Record<FeatureType, EntitlementObject>;
export type EntitlementPlansResponse = Record<FeatureType, NextPlanForEntitlement>;

export const DEFAULT_PRICE_PREVIEW: ProductPricesPreview = { dueToday: 0, upComingInvoice: 0 };

export enum SubscriptionEvent {
	Created = 'created',
	AutoUpgraded = 'auto_upgraded',
	CancelUpdates = 'cancel_updates',
	ManuallyUpgraded = 'manually_upgraded',
	ManuallyChanged = 'manually_changed',
	ManuallyCanceled = 'manually_cancelled',
	AutoCancelUpdates = 'auto_cancel_updates',
	ManuallyChangedUsersCount = 'manually_changed_users_count',
}

export interface ProductUsageHistory {
	plan_id: string;
	addons: unknown[];
	created_at: string;
	updated_at: string;
	unit_quantity: number | null;
	billing_period: BillingPeriod;
	is_billing_overages_enabled: boolean;
	subscription_event: SubscriptionEvent;
	is_team_tasks_virtually_unlimited: boolean;
	is_team_tasks_virtually_unlimited_changed: boolean;
}

export interface CapabilitiesDocumentation {
	id: FeatureTypes;
	title: string;
	description: string;
	howToFix: string;
	link?: string;
	linkText?: string;
}

export interface WorkflowCapabilitiesDocumentation {
	id: FeatureTypes;
	title: string;
	howToFix: string;
}
